import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { RefineModelModal } from '../components/RefineModelModal';
import { MobileNav } from '../components/MobileNav';
// Simple function to derive a mode from answers (placeholder logic)
const deriveSystemModel = (answers: string[]) => {
  // Count A vs B answers
  const aCount = answers.filter(a => a === 'A').length;
  const bCount = answers.filter(a => a === 'B').length;
  // Determine mode based on pattern
  if (Math.abs(aCount - bCount) <= 1) {
    return {
      mode: 'Oscillation',
      modeDescription: 'Your system is currently not settling into a stable regime. It tends to move between opposing pulls rather than staying locked into one operating mode.',
      patterns: [{
        name: 'Freedom vs Structure Loop',
        description: 'Your system shows a strong tension between wanting open space and wanting tight structure.'
      }, {
        name: 'Commitment vs Optionality Loop',
        description: 'Your system shows a recurring tension between locking in and keeping options open.'
      }],
      pressureDomain: 'Direction',
      pressureDescription: "Right now, most of the system's tension seems to be around where things are going, rather than how things are executed."
    };
  } else if (aCount > bCount) {
    return {
      mode: 'Convergence',
      modeDescription: 'Your system tends to narrow and lock in. It prefers defined structure and committed direction over open exploration.',
      patterns: [{
        name: 'Structure-First Pattern',
        description: 'Your system defaults to creating boundaries and constraints before exploring possibilities.'
      }, {
        name: 'Commitment Bias',
        description: 'Your system tends to commit early and push through resistance rather than reconsider direction.'
      }],
      pressureDomain: 'Execution',
      pressureDescription: "Right now, most of the system's tension seems to be around how things get done, rather than which direction to take."
    };
  } else {
    return {
      mode: 'Divergence',
      modeDescription: 'Your system tends to expand and explore. It prefers keeping options open and exploring multiple directions over locking in.',
      patterns: [{
        name: 'Exploration-First Pattern',
        description: 'Your system defaults to exploring possibilities before creating structure or committing to direction.'
      }, {
        name: 'Optionality Bias',
        description: 'Your system tends to keep options open and reconsider direction rather than push through resistance.'
      }],
      pressureDomain: 'Direction',
      pressureDescription: "Right now, most of the system's tension seems to be around where things are going, rather than how things are executed."
    };
  }
};
export function WorkMirrorResult() {
  const navigate = useNavigate();
  const [systemModel, setSystemModel] = useState<any>(null);
  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [modelConfidence, setModelConfidence] = useState<'Low' | 'Medium'>('Low');
  const [anchorsAdded, setAnchorsAdded] = useState(false);
  useEffect(() => {
    // Get answers from sessionStorage
    const answersJson = sessionStorage.getItem('workMirrorAnswers');
    if (!answersJson) {
      // No answers found, redirect to start
      navigate('/mirrors/work');
      return;
    }
    const answers = JSON.parse(answersJson);
    const model = deriveSystemModel(answers);
    setSystemModel(model);
    // Check if anchors were already added
    const anchorsAddedFlag = sessionStorage.getItem('workMirrorAnchorsAdded');
    if (anchorsAddedFlag === 'true') {
      setModelConfidence('Medium');
      setAnchorsAdded(true);
    }
  }, [navigate]);
  const handleRefineSubmit = (data: {
    dateOfBirth: string;
    timeOfBirth: string;
    placeOfBirth: string;
  }) => {
    // Store the anchor data
    sessionStorage.setItem('workMirrorAnchors', JSON.stringify(data));
    sessionStorage.setItem('workMirrorAnchorsAdded', 'true');
    // Close modal and show refining state
    setIsRefineModalOpen(false);
    setIsRefining(true);
    // Simulate refinement process (2 seconds)
    setTimeout(() => {
      setIsRefining(false);
      setModelConfidence('Medium');
      setAnchorsAdded(true);
    }, 2000);
  };
  if (!systemModel) {
    return null; // Loading or redirecting
  }
  if (isRefining) {
    return <>
        <Helmet data-id="element-4286">
          <title data-id="element-4287">Refining model... | Lazy Monk Labs</title>
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-4 sm:px-6" data-id="element-4288">
          <div className="max-w-2xl w-full text-center space-y-6 sm:space-y-8" data-id="element-4289">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#F5F5F5]" style={{
            fontFamily: 'Georgia, serif'
          }} data-id="element-4290">
              Refining model…
            </h1>

            <p className="text-base sm:text-lg text-gray-400" data-id="element-4291">
              Recomputing baseline with structural anchors.
            </p>

            {/* Subtle loading animation */}
            <div className="flex justify-center pt-6 sm:pt-8" data-id="element-4292">
              <div className="flex gap-2" data-id="element-4293">
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-pulse" style={{
                animationDelay: '0ms'
              }} data-id="element-4294" />
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-pulse" style={{
                animationDelay: '200ms'
              }} data-id="element-4295" />
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-pulse" style={{
                animationDelay: '400ms'
              }} data-id="element-4296" />
              </div>
            </div>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-4297">
        <title data-id="element-4298">Your Initial System — Work Mirror | Lazy Monk Labs</title>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4299">
        <MobileNav data-id="element-4300" />

        <main className="pt-20 sm:pt-24 pb-12 sm:pb-16" data-id="element-4301">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-12 sm:space-y-16" data-id="element-4302">
            {/* HEADER */}
            <div className="space-y-4 sm:space-y-6 pt-8 sm:pt-12" data-id="element-4303">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#F5F5F5] leading-tight" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-4304">
                Your initial system model
              </h1>
              <p className="text-sm sm:text-base text-gray-500 leading-relaxed" data-id="element-4305">
                This is a first-pass, low-confidence model. It will refine as
                you use the Mirror.
              </p>
            </div>

            {/* PRIMARY STATE BLOCK - CURRENT MODE */}
            <div className="border border-[#2B2B2B] p-6 sm:p-8 lg:p-10 space-y-4 sm:space-y-6 bg-[#0A0A0A]" data-id="element-4306">
              <div data-id="element-4307">
                <p className="text-xs font-mono text-gray-600 uppercase tracking-wider mb-2 sm:mb-3" data-id="element-4308">
                  Current Mode
                </p>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#F5F5F5] mb-4 sm:mb-6" data-id="element-4309">
                  {systemModel.mode}
                </h2>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-4310">
                  {systemModel.modeDescription}
                </p>
              </div>
            </div>

            {/* PHASE BLOCK */}
            <div className="border border-[#2B2B2B] p-6 sm:p-8 space-y-3 sm:space-y-4 bg-[#0A0A0A]" data-id="element-4311">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-4312">
                Current Phase
              </p>
              <p className="text-lg sm:text-xl text-[#F5F5F5]" data-id="element-4313">Initial Phase</p>
              <p className="text-xs sm:text-sm text-gray-500" data-id="element-4314">
                This is the starting phase of your Work Mirror.
              </p>
            </div>

            {/* PATTERNS BLOCK */}
            <div className="space-y-6 sm:space-y-8" data-id="element-4315">
              <div data-id="element-4316">
                <h3 className="text-xl sm:text-2xl font-serif text-[#F5F5F5] mb-2" data-id="element-4317">
                  Active Patterns (low confidence)
                </h3>
              </div>

              <div className="space-y-4 sm:space-y-6" data-id="element-4318">
                {systemModel.patterns.map((pattern: any, index: number) => <div key={index} className="border-l-2 border-[#2B2B2B] pl-4 sm:pl-6 space-y-2 sm:space-y-3" data-id="element-4319">
                    <p className="text-base sm:text-lg font-medium text-[#F5F5F5]" data-id="element-4320">
                      {pattern.name}
                    </p>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-4321">
                      {pattern.description}
                    </p>
                  </div>)}
              </div>

              <p className="text-xs sm:text-sm text-gray-600 italic pt-3 sm:pt-4" data-id="element-4322">
                These are early hypotheses. They will refine or disappear as
                more data comes in.
              </p>
            </div>

            {/* PRESSURE DOMAIN BLOCK */}
            <div className="border border-[#2B2B2B] p-6 sm:p-8 space-y-3 sm:space-y-4 bg-[#0A0A0A]" data-id="element-4323">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-4324">
                Current Pressure Domain
              </p>
              <p className="text-lg sm:text-xl text-[#F5F5F5]" data-id="element-4325">
                {systemModel.pressureDomain}
              </p>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-4326">
                {systemModel.pressureDescription}
              </p>
            </div>

            {/* CONFIDENCE BLOCK WITH REFINEMENT OPTION */}
            <div className="border border-[#2B2B2B] p-6 sm:p-8 space-y-4 sm:space-y-6 bg-[#0A0A0A]" data-id="element-4327">
              <div className="space-y-3 sm:space-y-4" data-id="element-4328">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3" data-id="element-4329">
                  <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-4330">
                    Model Confidence
                  </p>
                  {anchorsAdded && <span className="text-xs px-2 py-1 border border-[#2B2B2B] text-gray-500" data-id="element-4331">
                      Anchors added
                    </span>}
                </div>
                <p className="text-lg sm:text-xl text-[#F5F5F5]" data-id="element-4332">
                  {modelConfidence}
                </p>
                <p className="text-xs sm:text-sm text-gray-500" data-id="element-4333">
                  {modelConfidence === 'Low' ? 'This model is based only on a short calibration.' : 'This model uses structural anchors and calibration data.'}
                </p>
              </div>

              {/* Refinement option - only show if anchors not added */}
              {!anchorsAdded && <div className="pt-4 sm:pt-6 border-t border-[#2B2B2B] space-y-2 sm:space-y-3" data-id="element-4334">
                  <button onClick={() => setIsRefineModalOpen(true)} className="text-xs sm:text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors underline underline-offset-4" data-id="element-4335">
                    Increase precision
                  </button>
                  <p className="text-xs text-gray-600" data-id="element-4336">
                    Add structural anchors (like birth data) to increase the
                    resolution of the model.
                  </p>
                </div>}
            </div>

            {/* DIVIDER */}
            <div className="border-t border-[#2B2B2B]" data-id="element-4337" />

            {/* INSTRUCTION BLOCK */}
            <div className="space-y-4 sm:space-y-6" data-id="element-4338">
              <h3 className="text-xl sm:text-2xl font-serif text-[#F5F5F5]" data-id="element-4339">
                What happens next
              </h3>
              <div className="space-y-3 sm:space-y-4 text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-4340">
                <p data-id="element-4341">
                  From here, the Mirror builds itself from real situations,
                  states, and decisions.
                </p>
                <p data-id="element-4342">
                  You don't need to explain or analyze anything. Just drop
                  fragments of reality as they happen.
                </p>
              </div>
            </div>

            {/* PRIMARY CTA */}
            <div className="pt-6 sm:pt-8" data-id="element-4343">
              <Link to="/mirrors/work/app" className="block w-full sm:inline-block sm:w-auto px-8 sm:px-12 py-4 sm:py-5 bg-[#F5F5F5] text-[#0A0A0A] text-center font-medium text-sm tracking-widest uppercase hover:bg-gray-200 transition-colors" data-id="element-4344">
                Go to your Mirror
              </Link>
            </div>

            {/* SECONDARY LINK */}
            <div className="pt-3 sm:pt-4" data-id="element-4345">
              <Link to="/decoder" className="inline-flex items-center gap-2 text-xs sm:text-sm text-gray-600 hover:text-gray-400 transition-colors" data-id="element-4346">
                <span data-id="element-4347">How does this work?</span>
                <span data-id="element-4348">→</span>
              </Link>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-[#2B2B2B] mt-16 sm:mt-24" data-id="element-4349">
          <div className="max-w-6xl mx-auto" data-id="element-4350">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 sm:gap-8" data-id="element-4351">
              <div data-id="element-4352">
                <p className="text-sm text-gray-500 font-mono" data-id="element-4353">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-4354">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8 text-sm text-gray-500" data-id="element-4355">
                <a href="https://decoder-website.vercel.app/reports" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4356">
                  Decodes
                </a>
                <Link to="/mirrors/work" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4357">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4358">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4359">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4360">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-4361">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>

        {/* Refine Model Modal */}
        <RefineModelModal isOpen={isRefineModalOpen} onClose={() => setIsRefineModalOpen(false)} onSubmit={handleRefineSubmit} data-id="element-4362" />
      </div>
    </>;
}